'use client'

import Link from 'next/link'
import React, { useState } from 'react'

import type { Prodotto } from '@/payload-types'

import { useCarrello } from '@/negozio/carrello/CarrelloProvider.client'
import { formatEuro } from '@/negozio/prezzi'
import { mailto } from '@/utilities/siteInfo'

export type FormatoAcquisto = Pick<
  NonNullable<Prodotto['formati']>[number],
  'disponibilita' | 'etichetta' | 'prezzoCentesimi' | 'sku'
>

type Props = {
  nome: string
  suMisura: boolean
  prezziIndicativi: boolean
  /** Active formati only. */
  formati: FormatoAcquisto[]
}

const disponibilitaLabel: Record<FormatoAcquisto['disponibilita'], string> = {
  disponibile: 'Disponibile',
  'su-ordinazione': 'Su ordinazione',
  esaurito: 'Esaurito',
}

/**
 * Price, format choice and add-to-cart of the product page. Made-to-measure products also
 * ask for the paint (brand, vehicle model and colour code, all required as in the colour
 * finder), which becomes the cart line's `tintaLibera`.
 */
export const AcquistoProdotto: React.FC<Props> = ({
  nome,
  suMisura,
  prezziIndicativi,
  formati,
}) => {
  const [sku, setSku] = useState(
    () => (formati.find((f) => f.disponibilita !== 'esaurito') ?? formati[0])?.sku,
  )
  const [quantita, setQuantita] = useState(1)
  const [aggiunto, setAggiunto] = useState(false)
  const { aggiungi } = useCarrello()
  const formato = formati.find((f) => f.sku === sku) ?? formati[0]

  if (!formato) {
    return (
      <p className="modalnote">
        Questo prodotto al momento non è acquistabile online.{' '}
        <a href={mailto(`Informazioni su ${nome}`)}>Chiedi a noi ↗</a>
      </p>
    )
  }

  const esaurito = formato.disponibilita === 'esaurito'

  const selectQuantita = (
    <label>
      Quantità
      <select
        name="quantita"
        onChange={(e) => setQuantita(Number(e.target.value))}
        value={quantita}
      >
        {[1, 2, 3, 4, 5, 10].map((n) => (
          <option key={n}>{n}</option>
        ))}
      </select>
    </label>
  )

  const bottoneAggiungi = esaurito ? (
    <button className="primary" disabled type="button">
      Formato esaurito
    </button>
  ) : (
    <button className="primary" type="submit">
      Aggiungi al carrello <span>→</span>
    </button>
  )

  return (
    <div>
      <div className="detailprice">
        {formatEuro(formato.prezzoCentesimi)}{' '}
        <small className="modalnote">
          {prezziIndicativi ? 'Prezzo indicativo, IVA inclusa' : 'IVA inclusa'}
        </small>
      </div>

      {formati.length > 1 ? (
        <label>
          Formato
          <select
            name="formato"
            onChange={(e) => {
              setSku(e.target.value)
              setAggiunto(false)
            }}
            value={formato.sku}
          >
            {formati.map((f) => (
              <option key={f.sku} value={f.sku}>
                {f.etichetta} — {formatEuro(f.prezzoCentesimi)}
                {f.disponibilita === 'esaurito' ? ' (esaurito)' : ''}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p>
          Formato: <b>{formato.etichetta}</b>
        </p>
      )}
      <p className="modalnote">
        {disponibilitaLabel[formato.disponibilita]} · Codice {formato.sku}
      </p>

      {suMisura ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            const dati = new FormData(e.currentTarget)
            const campo = (nome: string) => String(dati.get(nome) ?? '').trim()
            aggiungi({
              sku: formato.sku,
              quantita,
              tintaLibera: {
                marca: campo('marca'),
                codice: campo('codice'),
                veicolo: campo('veicolo'),
              },
            })
            setAggiunto(true)
          }}
        >
          <label>
            Marca
            <input name="marca" pattern=".*\S.*" placeholder="Es. Fiat, Piaggio, RAL" required />
          </label>
          <label>
            Modello
            <input name="veicolo" pattern=".*\S.*" placeholder="Es. Grande Punto" required />
          </label>
          <label>
            Codice colore
            <input name="codice" pattern=".*\S.*" placeholder="Es. 199/A" required />
          </label>
          {selectQuantita}
          {bottoneAggiungi}
          <p className="modalnote">
            Prepariamo questo prodotto nel colore della tua auto o moto. Non conosci il codice?{' '}
            <Link href="/cercatinte">Cerca la tinta →</Link>
          </p>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            aggiungi({ sku: formato.sku, quantita })
            setAggiunto(true)
          }}
        >
          {selectQuantita}
          {bottoneAggiungi}
        </form>
      )}
      {aggiunto && (
        <p className="modalnote" role="status">
          Aggiunto al carrello. <Link href="/carrello">Vai al carrello →</Link>
        </p>
      )}
    </div>
  )
}
